import type { IconName, ModuleAccent } from '@nexus/core';
import { markdownSections, type MarkdownSection } from '@nexus/ui';
import { queryOptions, useQueries } from '@tanstack/react-query';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { visibleSections } from '../admin/sections';
import type { SessionInfo } from '../api/types';
import type { GuideSource, WebGuide, WebModule } from '../modules/define';
import { useModules } from '../modules/context';
import { canIn, useSession } from '../session/session';
import { KERNEL_GUIDES } from './kernel';

/** A guide the person may read, with the area it belongs to. */
export interface VisibleGuide {
  guide: WebGuide;
  /** `core.basics`, `core.admin` or the ID of the main module. */
  area: string;
  /** i18n key of the area. */
  areaLabel: string;
  icon: IconName;
  accent?: ModuleAccent;
  /** Module of the guide, also for submodules; `core` for the kernel. */
  owner: string;
}

/**
 * Guides of the kernel and of the active modules the person may see: kernel guides of
 * administration pages need one of those pages, module guides their permission.
 */
export function visibleGuides(modules: readonly WebModule[], session: SessionInfo): VisibleGuide[] {
  const sections = new Set(visibleSections(session, modules).map((s) => s.id));
  const kernel = KERNEL_GUIDES.filter(
    (guide) => !guide.sections || guide.sections.some((id) => sections.has(id)),
  ).map((guide): VisibleGuide => ({
    guide,
    area: guide.sections ? 'core.admin' : 'core.basics',
    areaLabel: guide.sections ? 'help.areas.admin' : 'help.areas.basics',
    icon: guide.sections ? 'settings' : 'book-open',
    owner: 'core',
  }));

  const active = new Set(session.modules);
  const fromModules = modules.flatMap((module) => {
    const main = module.manifest.parent
      ? modules.find((m) => m.manifest.id === module.manifest.parent)
      : module;
    if (!main || !active.has(module.manifest.id) || !active.has(main.manifest.id)) return [];
    return (module.guides ?? [])
      .filter((guide) => !guide.permission || canIn(session, guide.permission))
      .map((guide): VisibleGuide => ({
        guide,
        area: main.manifest.id,
        areaLabel: main.manifest.name,
        icon: main.manifest.icon,
        accent: main.manifest.theme?.accent,
        owner: module.manifest.id,
      }));
  });
  return [...kernel, ...fromModules];
}

export function useVisibleGuides(): VisibleGuide[] {
  const modules = useModules();
  const session = useSession();
  return useMemo(() => visibleGuides(modules, session), [modules, session]);
}

/** Checks the guides of the modules when the router is built; mistakes fail at start. */
export function checkGuides(modules: readonly WebModule[]): void {
  const ids = new Set(KERNEL_GUIDES.map((g) => g.id));
  for (const module of modules) {
    const own = new Set(module.pages.map((page) => page.path));
    for (const guide of module.guides ?? []) {
      if (!guide.id.startsWith(`${module.manifest.id}.`)) {
        throw new Error(`Guide ${guide.id} must start with "${module.manifest.id}."`);
      }
      if (!/^[a-z0-9.-]+$/.test(guide.id) || ids.has(guide.id)) {
        throw new Error(`Guide ${guide.id}: invalid or duplicate ID`);
      }
      ids.add(guide.id);
      for (const page of guide.pages ?? []) {
        if (!own.has(page)) throw new Error(`Guide ${guide.id}: ${page} is no page of the module`);
      }
    }
  }
}

/**
 * The guide for the page on screen: one that names the page, else the only guide of its module,
 * else the guides of the module in the help area.
 */
export function guideForPage(
  guides: readonly VisibleGuide[],
  modules: readonly WebModule[],
  route: string,
): { guideId: string } | { area: string } | null {
  const named = guides.find((g) => g.guide.pages?.includes(route));
  if (named) return { guideId: named.guide.id };
  const module = modules.find((m) => m.pages.some((page) => page.path === route));
  if (!module) return null;
  const area = module.manifest.parent ?? module.manifest.id;
  const ofModule = guides.filter((g) => g.area === area);
  if (ofModule.length === 1) return { guideId: ofModule[0]!.guide.id };
  return ofModule.length > 1 ? { area } : null;
}

export type GuideLocale = 'de' | 'en';

export function guideLocale(language: string | undefined): GuideLocale {
  return language?.startsWith('en') ? 'en' : 'de';
}

async function load(source: GuideSource): Promise<string> {
  const loaded = await source();
  return typeof loaded === 'string' ? loaded : loaded.default;
}

export function guideQuery(guide: WebGuide, locale: GuideLocale) {
  return queryOptions({
    queryKey: ['guide', guide.id, locale],
    queryFn: () => load(guide.content[locale]),
    staleTime: Infinity,
    gcTime: Infinity,
  });
}

// Stable functions, so React Query keeps the parsed sections between renders.
const toSections = (source: string) => markdownSections(source);
const sectionsOf = (results: { data?: MarkdownSection[] }[]) => results.map((r) => r.data);

export interface LoadedGuide extends VisibleGuide {
  title: string;
  summary: string;
  /** Empty until the text has loaded. */
  sections: MarkdownSection[];
}

/** Texts of the visible guides in the language of the app, loaded once `enabled`. */
export function useGuideTexts(guides: readonly VisibleGuide[], enabled: boolean): LoadedGuide[] {
  const { t, i18n } = useTranslation();
  const locale = guideLocale(i18n.language);
  const sections = useQueries({
    queries: guides.map((g) => ({ ...guideQuery(g.guide, locale), select: toSections, enabled })),
    combine: sectionsOf,
  });
  return useMemo(
    () =>
      guides.map((g, index) => ({
        ...g,
        title: t(g.guide.title),
        summary: t(g.guide.summary),
        sections: sections[index] ?? [],
      })),
    [guides, t, sections],
  );
}

export interface GuideHit {
  guide: LoadedGuide;
  /** The section that matched, or none when the title or summary did. */
  section?: MarkdownSection;
  /** Text around the match. */
  snippet: string;
  score: number;
}

/** Lower case without accents, `ß` as `ss`, with the position of every character in the input. */
function fold(text: string): { value: string; at: number[] } {
  let value = '';
  const at: number[] = [];
  for (let i = 0; i < text.length; i++) {
    const folded = text[i]!.toLowerCase()
      .replace('ß', 'ss')
      .normalize('NFD')
      .replace(/\p{M}/gu, '');
    value += folded;
    for (let n = 0; n < folded.length; n++) at.push(i);
  }
  return { value, at };
}

export function searchWords(term: string): string[] {
  return fold(term)
    .value.split(/[^\p{L}\p{N}]+/u)
    .filter(Boolean);
}

const SNIPPET = 160;

function snippet(text: string, words: readonly string[]): string {
  const folded = fold(text);
  const found = words.map((w) => folded.value.indexOf(w)).filter((i) => i >= 0);
  const position = folded.at[found.length ? Math.min(...found) : 0] ?? 0;
  if (text.length <= SNIPPET) return text;
  let start = Math.max(0, position - 50);
  if (start > 0) start = text.indexOf(' ', start) + 1 || start;
  const end = Math.min(text.length, start + SNIPPET);
  return `${start > 0 ? '… ' : ''}${text.slice(start, end).trim()}${end < text.length ? ' …' : ''}`;
}

/**
 * Finds guides by title, summary and the text of their sections. Every word must occur, also
 * as part of a longer word; umlauts and accents do not matter. At most two sections per guide.
 */
export function searchGuides(guides: readonly LoadedGuide[], term: string): GuideHit[] {
  const words = searchWords(term);
  if (!words.length) return [];
  const all = (text: string) => {
    const value = fold(text).value;
    return words.every((w) => value.includes(w));
  };
  const hits: GuideHit[] = [];
  for (const guide of guides) {
    // When the guide itself matches, only sections that match on their own are worth listing.
    const head = all(`${guide.title} ${guide.summary}`);
    if (head) hits.push({ guide, snippet: guide.summary, score: all(guide.title) ? 4 : 3 });
    const context = head ? '' : guide.title;
    hits.push(
      ...guide.sections
        .filter((section) => all(`${context} ${section.title} ${section.text}`))
        .map((section) => ({
          guide,
          section,
          snippet: snippet(section.text, words),
          score: all(section.title) ? 2 : 1,
        }))
        .sort((a, b) => b.score - a.score)
        .slice(0, 2),
    );
  }
  return hits.sort((a, b) => b.score - a.score);
}

/** Address of a hit: the guide, scrolled to the section. */
export function guideHref(hit: { guide: { guide: WebGuide }; section?: MarkdownSection }): string {
  const base = `/help/${hit.guide.guide.id}`;
  return hit.section?.id ? `${base}#${hit.section.id}` : base;
}
